import type { LocationQuery, LocationQueryRaw } from 'vue-router';

export function useQueryParams() {
  const route = useRoute();
  const router = useRouter();

  /**
   * Add or update query parameters.
   *
   * Example: `setParams({ page: 2 })`
   * Result: `?page=2` is merged into the current query string.
   *
   * @param params Key/value pairs to merge into the query string.
   * @param options.replace If true, replaces the current history entry. Defaults to false (push).
   */
  function setParams(params: LocationQueryRaw, { replace = false } = {}) {
    const mergedQuery: LocationQueryRaw = { ...route.query, ...params };
    return navigateToQuery(mergedQuery, replace);
  }

  /**
   * Remove query parameters.
   *
   * Example: `removeParams(['page', 'sort'])`
   * Result: removes both keys from the current query string.
   *
   * @param keys Array of parameter names to remove.
   * @param options.replace If true, replaces the current history entry. Defaults to false (push).
   */
  function removeParams(keys: string[], { replace = false } = {}) {
    const filteredQuery = Object.fromEntries(
      Object.entries(route.query).filter(([k]) => !keys.includes(k))
    ) as LocationQueryRaw;

    return navigateToQuery(filteredQuery, replace);
  }

  /**
   * Internal: navigates only if the new query differs from the current one.
   *
   * @param targetQuery The new query object to navigate to.
   * @param replace If true, replaces the current history entry.
   */
  function navigateToQuery(targetQuery: LocationQueryRaw, replace: boolean) {
    if (queriesAreEqual(route.query, targetQuery)) {
      return Promise.resolve();
    }
    return router[replace ? 'replace' : 'push']({
      path: route.path,
      query: targetQuery
    });
  }

  /**
   * Normalize a query object for comparison.
   * Ensures each value is an array of strings, sorted for order-insensitivity.
   *
   * Example:
   * `{ tags: ['b', 'a'] }` -> `{ tags: ['a', 'b'] }`
   *
   * Null and undefined values are discarded.
   *
   * @param query Raw query object (from route or target).
   * @returns Normalized query with arrays of sorted strings.
   */
  function normalizeQuery(query: LocationQuery | LocationQueryRaw): Record<string, string[]> {
    const normalized: Record<string, string[]> = {};
    for (const key in query) {
      const rawValue = query[key];
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      normalized[key] = values
        .filter((v): v is string | number => v != null) // filter out null/undefined
        .map(String)
        .sort();
    }
    return normalized;
  }

  /**
   * Compare two query objects for semantic equality.
   * Arrays are compared without regard to order.
   *
   * Example:
   * `{ tags: ['a', 'b'] } === { tags: ['b', 'a'] }` -> true
   *
   * @param queryA First query object.
   * @param queryB Second query object.
   * @returns True if both queries are equal in keys and values.
   */
  function queriesAreEqual(
    queryA: LocationQuery | LocationQueryRaw,
    queryB: LocationQuery | LocationQueryRaw
  ): boolean {
    const normalizedA = normalizeQuery(queryA);
    const normalizedB = normalizeQuery(queryB);

    const keysA = Object.keys(normalizedA);
    const keysB = Object.keys(normalizedB);
    if (keysA.length !== keysB.length) return false;

    return keysA.every(
      (key) =>
        keysB.includes(key) &&
        normalizedA[key]!.length === normalizedB[key]!.length &&
        normalizedA[key]!.every((value, index) => value === normalizedB[key]![index])
    );
  }

  return { setParams, removeParams };
}
